import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Tag, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { posts } from '../lib/blog/posts';
import Footer from '../components/landing/Footer';

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'uz') as 'uz' | 'ru' | 'en';

  const postIndex = posts.findIndex(p => p.slug === slug);
  const post = posts[postIndex];

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const nextPost = posts[postIndex + 1] || posts[0];
  const prevPost = postIndex > 0 ? posts[postIndex - 1] : posts[posts.length - 1];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title[lang],
    description: post.description[lang],
    author: {
      '@type': 'Organization',
      name: post.author,
      url: 'https://pmaster.uz',
    },
    publisher: {
      '@type': 'Organization',
      name: 'PMaster',
      url: 'https://pmaster.uz',
      logo: { '@type': 'ImageObject', url: 'https://pmaster.uz/logo.png' },
    },
    datePublished: post.publishedAt,
    dateModified: post.updatedAt,
    mainEntityOfPage: `https://pmaster.uz/blog/${post.slug}`,
    inLanguage: lang,
    keywords: post.tags.join(', '),
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <html lang={lang} />
        <title>{post.title[lang]} | pMaster Blog</title>
        <meta name="description" content={post.description[lang]} />
        <meta property="og:title" content={post.title[lang]} />
        <meta property="og:description" content={post.description[lang]} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://pmaster.uz/blog/${post.slug}`} />
        <meta property="og:image" content="https://pmaster.uz/og-image.png" />
        <meta property="og:site_name" content="PMaster" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title[lang]} />
        <meta name="twitter:description" content={post.description[lang]} />
        <meta name="twitter:image" content="https://pmaster.uz/og-image.png" />
        <meta property="article:published_time" content={post.publishedAt} />
        <meta property="article:modified_time" content={post.updatedAt} />
        <meta property="article:author" content={post.author} />
        {post.tags.map(tag => (
          <meta key={tag} property="article:tag" content={tag} />
        ))}
        <link rel="canonical" href={`https://pmaster.uz/blog/${post.slug}`} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>

      {/* Hero header */}
      <div className="bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 text-white">
        <div className="max-w-3xl mx-auto px-4 pt-6 pb-12 md:pb-16">
          <Link to="/blog" className="inline-flex items-center gap-2 text-blue-300 hover:text-white text-sm mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            {t('backToBlog')}
          </Link>

          <div className="flex flex-wrap gap-2 mb-4">
            {post.tags.map(tag => (
              <span key={tag} className="px-2.5 py-1 bg-blue-800/50 text-blue-200 text-xs font-medium rounded-full border border-blue-700/50">
                {tag}
              </span>
            ))}
          </div>

          <h1 className="text-3xl md:text-4xl font-bold leading-tight mb-4">
            {post.title[lang]}
          </h1>
          <p className="text-blue-200 text-lg">{post.description[lang]}</p>

          <div className="mt-6 flex items-center gap-5 text-sm text-blue-300">
            <span className="flex items-center gap-1.5">
              <User className="w-4 h-4" />
              {post.author}
            </span>
            <span className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              {new Date(post.publishedAt).toLocaleDateString(lang === 'uz' ? 'uz-UZ' : lang === 'ru' ? 'ru-RU' : 'en-US', {
                year: 'numeric', month: 'long', day: 'numeric'
              })}
            </span>
          </div>
        </div>
      </div>

      {/* Article content */}
      <div className="max-w-3xl mx-auto px-4 py-10 md:py-14">
        <article
          className="prose prose-lg max-w-none
            prose-headings:text-gray-900 prose-headings:font-bold prose-headings:mt-8 prose-headings:mb-4
            prose-h2:text-2xl prose-h2:border-b prose-h2:border-gray-100 prose-h2:pb-3
            prose-p:text-gray-700 prose-p:leading-relaxed
            prose-li:text-gray-700 prose-li:leading-relaxed
            prose-a:text-blue-600 prose-a:font-medium prose-a:no-underline hover:prose-a:underline
            prose-strong:text-gray-900
            prose-table:text-sm prose-table:border prose-table:border-gray-200 prose-table:rounded-lg prose-table:overflow-hidden
            prose-th:bg-gray-50 prose-th:p-3 prose-th:text-left prose-th:font-semibold
            prose-td:p-3 prose-td:border-t prose-td:border-gray-100
            prose-blockquote:border-l-blue-500 prose-blockquote:bg-blue-50/50 prose-blockquote:py-1 prose-blockquote:px-4 prose-blockquote:rounded-r-lg
            prose-ul:pl-0 prose-li:pl-0"
          dangerouslySetInnerHTML={{ __html: post.content[lang] }}
        />

        {/* CTA */}
        <div className="mt-12 p-8 bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl text-center text-white">
          <h3 className="text-xl font-bold mb-2">
            {lang === 'uz' ? "pMaster bilan ustaxonangizni boshqaring" :
             lang === 'ru' ? "Управляйте мастерской с pMaster" :
             "Manage your repair shop with pMaster"}
          </h3>
          <p className="text-blue-100 text-sm mb-5">
            {lang === 'uz' ? "Bepul rejada hoziroq boshlang — karta talab qilinmaydi" :
             lang === 'ru' ? "Начните бесплатно прямо сейчас — карта не нужна" :
             "Start free right now — no credit card required"}
          </p>
          <Link
            to="/auth/signup"
            className="inline-flex items-center gap-2 px-8 py-3 bg-white text-blue-700 font-semibold rounded-lg hover:bg-blue-50 transition-colors"
          >
            {lang === 'uz' ? "Ro'yxatdan o'tish" :
             lang === 'ru' ? "Зарегистрироваться" :
             "Sign up free"}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Navigation between posts */}
        <div className="mt-10 grid grid-cols-2 gap-4 pt-8 border-t border-gray-100">
          <Link
            to={`/blog/${prevPost.slug}`}
            className="group p-4 rounded-xl hover:bg-gray-50 transition-colors"
          >
            <span className="text-xs text-gray-400 flex items-center gap-1 mb-1">
              <ArrowLeft className="w-3 h-3" />
              {lang === 'uz' ? "Oldingi" : lang === 'ru' ? "Предыдущая" : "Previous"}
            </span>
            <span className="text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors line-clamp-2">
              {prevPost.title[lang]}
            </span>
          </Link>
          <Link
            to={`/blog/${nextPost.slug}`}
            className="group p-4 rounded-xl hover:bg-gray-50 transition-colors text-right"
          >
            <span className="text-xs text-gray-400 flex items-center justify-end gap-1 mb-1">
              {lang === 'uz' ? "Keyingi" : lang === 'ru' ? "Следующая" : "Next"}
              <ArrowRight className="w-3 h-3" />
            </span>
            <span className="text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors line-clamp-2">
              {nextPost.title[lang]}
            </span>
          </Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}
